import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import Lenis from "lenis";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import MarqueeStrip from "@/components/Marquee";
import Welcome from "@/components/Welcome";
import Tours from "@/components/Tours";
import Groups from "@/components/Groups";
import BannerSection from "@/components/BannerSection";
import Gallery from "@/components/Gallery";
import VideoSection from "@/components/VideoSection";
import Testimonials from "@/components/Testimonials";
import Faq from "@/components/Faq";
import Contact from "@/components/Contact";
import Footer from "@/components/Footer";
import Concierge from "@/components/Concierge";
import ScrollToTop from "@/components/ScrollToTop";
import Seo from "@/components/Seo";
import { EXPERIENCES } from "@/data/experiences";
import { GROUPS } from "@/data/groups";
import ExperiencesPage from "@/pages/ExperiencesPage";
import ExperiencePage from "@/pages/ExperiencePage";
import GroupPage from "@/pages/GroupPage";
import PackagesPage from "@/pages/PackagesPage";
import IslandPage from "@/pages/IslandPage";
import LodgePage from "@/pages/LodgePage";
import GettingHerePage from "@/pages/GettingHerePage";
import ContactPage from "@/pages/ContactPage";
import VouchersPage from "@/pages/VouchersPage";
import VoucherSuccessPage from "@/pages/VoucherSuccessPage";
import ThankYouPage from "@/pages/ThankYouPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-navy-800 px-6 text-center">
          <div>
            <p className="font-serif text-3xl text-ivory">The Adventure Islands</p>
            <p className="mt-3 text-sm text-mist">
              Something went adrift. Please refresh the page.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Home() {
  return (
    <main>
      <Hero />
      <MarqueeStrip />
      <Welcome />
      <Tours />
      <Groups />
      <BannerSection />
      <Gallery />
      <VideoSection />
      <Testimonials />
      <Faq />
      <Contact />
    </main>
  );
}

function ExperienceRoute() {
  const { slug } = useParams();
  const item = EXPERIENCES.find((e) => e.slug === slug);
  return (
    <>
      {item && (
        <Seo
          title={`${item.title} | The Adventure Islands`}
          description={item.short}
          path={`/experiences/${item.slug}`}
        />
      )}
      <ExperiencePage />
    </>
  );
}

function GroupRoute() {
  const { slug } = useParams();
  const item = GROUPS.find((g) => g.slug === slug);
  return (
    <>
      {item && (
        <Seo
          title={`${item.title} | The Adventure Islands`}
          description={item.short}
          path={`/groups/${item.slug}`}
        />
      )}
      <GroupPage />
    </>
  );
}

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);

    const onClick = (e) => {
      const anchor = e.target.closest && e.target.closest('a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute("href");
      if (!hash || hash.length < 2) return;
      const el = document.querySelector(hash);
      if (!el) return;
      e.preventDefault();
      lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    };
    document.addEventListener("click", onClick);
    return () => {
      document.removeEventListener("click", onClick);
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="min-h-screen bg-navy-800 font-sans text-ivory antialiased">
      <div className="noise-overlay" aria-hidden="true" />
      <ErrorBoundary>
        <BrowserRouter>
          <ScrollToTop />
          <Navbar />
          <Routes>
            <Route path="/" element={<><Seo title="The Adventure Islands | Private Island Adventures, Clew Bay, Westport" description="Sea safaris, high-speed RIB tours, kayaking, SUP and beach saunas on our private island in Clew Bay — 12 minutes from Westport, Co. Mayo. All ages 8+." path="/" /><Home /></>} />
            <Route path="/experiences" element={<><Seo title="Adventure Tours & Experiences, Clew Bay | The Adventure Islands" description="Choose your Clew Bay adventure — sea safaris, high-speed RIB tours, kayaking, SUP and beach sauna on our private island, 12 minutes from Westport." path="/experiences" /><ExperiencesPage /></>} />
            <Route path="/experiences/:slug" element={<ExperienceRoute />} />
            <Route path="/group-packages" element={<><Seo title="Group Packages & Prices | The Adventure Islands" description="Group adventure packages on Collanmore Island — corporate team building, school tours, hen & stag parties and family adventures, all coordinated for you." path="/group-packages" /><PackagesPage /></>} />
            <Route path="/groups/:slug" element={<GroupRoute />} />
            <Route path="/the-island" element={<><Seo title="Our Private Island in Clew Bay | The Adventure Islands" description="Collanmore Island sits in the heart of Clew Bay beneath Croagh Patrick — two minutes by high-speed RIB from Rosmoney Pier, a world away from ordinary." path="/the-island" /><IslandPage /></>} />
            <Route path="/the-lodge" element={<><Seo title="The Lodge on Collanmore Island | The Adventure Islands" description="Tom's Bar, modern bedrooms, hot tubs and saunas — The Lodge on Collanmore Island sleeps up to 22 guests for private island stays in Clew Bay." path="/the-lodge" /><LodgePage /></>} />
            <Route path="/getting-here" element={<><Seo title="Getting Here — Directions to Rosmoney Pier, Westport" description="How to reach The Adventure Islands: five driving routes to Rosmoney Pier near Westport, plus parking, transit and the two-minute RIB island crossing." path="/getting-here" /><GettingHerePage /></>} />
            <Route path="/contact" element={<><Seo title="Contact & Bookings | The Adventure Islands, Westport" description="Book your island adventure: call +353 86 251 8252, email info@theadventureislands.com or send an enquiry — we're open daily 09:00-18:00, 365 days a year." path="/contact" /><ContactPage /></>} />
            <Route path="/vouchers" element={<><Seo title="Gift Vouchers — The Perfect Christmas Gift | The Adventure Islands" description="Give an island adventure this Christmas: gift vouchers for sea safaris, high-speed RIB tours, kayaking, SUP and beach sauna on Collanmore Island, Clew Bay. Valid 12 months." path="/vouchers" /><VouchersPage /></>} />
            <Route path="/voucher/success" element={<VoucherSuccessPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/payment/success" element={<PaymentSuccessPage />} />
            <Route path="/payment/cancel" element={<PaymentCancelPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Footer />
          <Concierge />
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
